<template>
	<section
		:class="['featured-asset-panel bg-color-greige marketing-section', { 'theme--light': !darkText }]"
		:style="`background-image: url(${appCloudfrontPath + assetInfo.backgroundUrl});`"
	>
		<div class="container position-relative">
			<div class="grid-md gutters-300-md">
				<div class="col-9-md col-6-lg">
					<h2 :class="['heading-2 text-align-center-smo', { 'text-color-white': !darkText }]">
						{{ assetInfo.heading }}
					</h2>
					<p
						:class="[
							'text-align-center-smo gamma-md font-weight-lighter-md mt-50-md',
							{ 'text-color-white': !darkText }
						]"
					>
						{{ assetInfo.description }}
					</p>
					<BasePlaceHolder
						:placehold="assetLoading"
						:line-count="2"
						class="display-flex align-items-stretch content-block justify-content-center-smo"
						:class="{ 'text-color-white': !darkText }"
					>
						<div v-if="featuredAsset && showStrategySection">
							<h3>Strategy</h3>
							<p class="gamma mt-0">
								<span
									class="strategy-badge--large mr-25 display-inline-block"
									:style="{ 'background-color': strategyColor }"
								></span>
								{{ featuredAsset.realEstateAndPrivateCreditFields?.strategy.name }}
							</p>
						</div>
						<div
							:class="[
								'bl ml-100 pl-100 display-none-smo text-align-center',
								{ 'border-color-reversed': !darkText }
							]"
						>
							<BaseLinkButton
								:classes="`button ${darkText ? 'button-outline' : 'button-outline-reversed'} delta`"
								@click="openAssetModal()"
							>
								View details
							</BaseLinkButton>
						</div>
					</BasePlaceHolder>
					<div v-if="assetInfo.assetImages" class="grid gutters-100 content-block" role="presentation">
						<div class="col-8">
							<div class="aspect-ratio-4-3" role="figure">
								<img
									class="img-lazy-inline rounded deep-box-shadow fade in"
									:lazy-src="appCloudfrontPath + assetInfo.assetImages[0]"
									aria-hidden="false"
									data-acsb-hidden="false"
									:src="appCloudfrontPath + assetInfo.assetImages[0]"
									alt=""
									role="presentation"
								/>
							</div>
						</div>
						<div class="col-4">
							<div class="aspect-ratio-4-3" role="figure">
								<img
									class="img-lazy-inline rounded deep-box-shadow fade in"
									:lazy-src="appCloudfrontPath + assetInfo.assetImages[1]"
									aria-hidden="false"
									:src="appCloudfrontPath + assetInfo.assetImages[1]"
									alt=""
									role="presentation"
								/>
							</div>
							<div class="aspect-ratio-4-3" role="figure">
								<img
									class="img-lazy-inline rounded deep-box-shadow fade in mt-75"
									:lazy-src="appCloudfrontPath + assetInfo.assetImages[2]"
									aria-hidden="false"
									:src="appCloudfrontPath + assetInfo.assetImages[2]"
									alt=""
									role="presentation"
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>
<script setup lang="ts">
import { computed, ref, Ref } from 'vue';
import { appCloudfrontPath } from '@constants';
import { Asset } from 'types/assets';
import AssetElementModal from '@components/asset-element/asset-element-modal.vue';
import { FeaturedAsset } from 'types/layout';
import { getAsset } from '@api/sanity/proxy/assets';
import { realEstateStrategyColor } from '@filters/shared-filters';
import { RealEstateStrategySlug } from 'types/real-estate-strategy';
import { useAppStore } from '@stores/app';

interface Props {
	assetInfo: FeaturedAsset;
	darkText?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
	darkText: true
});

const appStore = useAppStore();

const featuredAsset: Ref<Asset | null> = ref(null);
const assetLoading = ref(true);

const showStrategySection = computed((): boolean => {
	return !!featuredAsset.value?.realEstateAndPrivateCreditFields?.strategy;
});

const strategyColor = computed((): string => {
	return realEstateStrategyColor(
		featuredAsset.value?.realEstateAndPrivateCreditFields?.strategy.slug as RealEstateStrategySlug
	);
});

function openAssetModal(): void {
	if (featuredAsset.value) {
		appStore.updateCurrentModal({
			modal: AssetElementModal,
			props: { assetElement: featuredAsset.value }
		});
	}
}

async function setup(): Promise<void> {
	featuredAsset.value = await getAsset(props.assetInfo.assetId, { elementType: props.assetInfo.elementType });
	assetLoading.value = false;
}

setup();
</script>

<style lang="scss">
@use '../../styles/utilities/respond-to.scss' as *;

.featured-asset-panel {
	background-position: bottom;
	background-repeat: no-repeat;
	background-size: cover;
	position: relative;

	&::before {
		background-color: #fff;
		content: '';
		height: 100%;
		left: 0;
		opacity: 0.5;
		position: absolute;
		top: 0;
		width: 100%;

		@include respond-to(md) {
			opacity: 0;
		}
	}

	&.theme--light {
		&::before {
			background-color: #000;
		}
	}

	.bl {
		border-color: rgba(0, 0, 0, 0.2);
	}
}
</style>
